import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import splashIcon from "../assets/images/splash-icon.png";
import splashIconDark from "../assets/images/ToronQ Logos-15.png";
import {
  MapPin as FaMapMarkerAlt,
  Search as FaSearch,
  Star as FaStar,
  Check as FaCheck,
  Square as FaSquare,
  CheckSquare as FaCheckSquare,
  Building2 as FaBuilding,
} from "lucide-react";
import { searchBusinessApi } from "../api/business.api";
import {
  getBusinessId,
  getBusinessName,
  getBusinessAddress,
  getBusinessRating,
  getBusinessReviewCount,
} from "../utils/business";
import { useTheme } from "../context/ThemeContext";
import Button from "../components/ui/Button";
import type { ApiError } from "../types/api";
import type { RawBusiness } from "../types/business";

export default function SearchBusiness() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, mode } = useTheme();
  // Neutral border for structure — theme.border carries a blue tint meant for
  // accent surfaces; this page uses it only where blue is intentional.
  const subtleBorder = mode === "dark" ? theme.border : "#E7EAF0";

  // Get mode from navigation state
  const searchMode = location.state?.mode || "new-business"; // "new-business" or "add-location"
  const parentBusiness = location.state?.parentBusiness || null;
  const prefillSearch = location.state?.prefillSearch || "";

  const isAddLocationMode = searchMode === "add-location" && parentBusiness;
  const onboardingContext =
    location.state?.onboardingContext ||
    JSON.parse(localStorage.getItem("onboarding_context") || "null");
  const roleLabel = onboardingContext?.roleLabel || "Business";

  const [query, setQuery] = useState(prefillSearch);
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState<RawBusiness[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<RawBusiness[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-search if prefill is provided
  useEffect(() => {
    if (prefillSearch && !hasSearched) {
      handleSearch();
    }
  }, []);

  useEffect(() => {
    if (!isAddLocationMode) {
      navigate("/onboarding", { replace: true });
    }
  }, [isAddLocationMode, navigate]);

  const handleSearch = async () => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery || isLoading) return;

    setHasSearched(true);
    setSelectedLocations([]);
    setIsLoading(true);
    setError("");

    try {
      const response = await searchBusinessApi(trimmedQuery);

      setResults(response?.data || []);
    } catch (err) {
      // If this is a 401 error, the axios interceptor will handle redirect to login
      // Don't show error message as user will be redirected
      if ((err as ApiError)?.response?.status === 401) {
        return;
      }
      setResults([]);
      setError(
        (err as ApiError)?.response?.data?.message || "Unable to search locations.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLocationSelection = (loc: RawBusiness) => {
    const locationId = getBusinessId(loc);
    const isSelected = selectedLocations.some(
      (item) => getBusinessId(item) === locationId
    );

    if (isSelected) {
      setSelectedLocations(
        selectedLocations.filter((item) => getBusinessId(item) !== locationId)
      );
    } else {
      setSelectedLocations([...selectedLocations, loc]);
    }
  };

  const handleSelectAll = () => {
    if (selectedLocations.length === results.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations([...results]);
    }
  };

  const handleConnectLocations = () => {
    if (selectedLocations.length === 0) return;

    localStorage.setItem("selected_locations", JSON.stringify(selectedLocations));

    // For single location, use existing flow
    if (selectedLocations.length === 1) {
      localStorage.setItem("selected_business", JSON.stringify(selectedLocations[0]));
      navigate("/connect-google", {
        state: {
          business: selectedLocations[0],
          mode: searchMode,
          parentBusiness,
        },
      });
    } else {
      // For multiple locations, pass all
      navigate("/connect-google", {
        state: {
          locations: selectedLocations,
          isMultiLocation: true,
          mode: searchMode,
          parentBusiness,
        },
      });
    }
  };

  return (
    <div className="min-h-screen px-4 py-8" style={{ background: theme.background }}>
      <div className="mx-auto w-full max-w-3xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-8 text-sm font-semibold"
          style={{ color: theme.primary }}
        >
          ← Back
        </button>

        <div className="text-center">
          <img
            src={mode === "dark" ? splashIconDark : splashIcon}
            alt="ToronQ logo"
            className="mx-auto mt-6 h-16 w-auto object-contain"
          />

          <h1 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
            {isAddLocationMode ? "Add New Location" : "Select Locations"}
          </h1>

          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 sm:text-base" style={{ color: theme.textMuted }}>
            {isAddLocationMode
              ? `Search for additional locations to add to ${parentBusiness?.name}`
              : `Step 4: ${roleLabel} flow. Search and select the locations you want to connect with ToronQ AI.`}
          </p>
        </div>

        {/* Show parent business context when adding location */}
        {isAddLocationMode && (
          <div className="mt-6 flex items-center gap-3 rounded-2xl border px-4 py-3" style={{ borderColor: subtleBorder, background: theme.secondary }}>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: theme.infoBg }}>
              <FaBuilding size={18} color={theme.primary} />
            </div>
            <div>
              <p className="text-xs font-medium" style={{ color: theme.textLight }}>Adding to</p>
              <p className="font-semibold" style={{ color: theme.text }}>{parentBusiness?.name}</p>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="flex items-center rounded-xl border px-4" style={{ borderColor: subtleBorder, background: theme.card }}>
            <FaSearch size={18} color={theme.primary} />

            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isAddLocationMode ? `Search ${parentBusiness?.name} locations...` : "Search business name..."}
              className="flex-1 bg-transparent py-4 pl-3 text-sm outline-none"
              style={{ color: theme.text }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </div>

          <Button
            variant="primary"
            theme={theme}
            onClick={handleSearch}
            disabled={!query.trim() || isLoading}
            className="mt-5 w-full py-4 text-base"
          >
            {isLoading ? "Searching..." : "Search Locations"}
          </Button>
        </div>

        {error && (
          <p className="mt-4 rounded-xl px-4 py-3 text-sm font-medium" style={{ background: theme.dangerBg, color: theme.danger }}>
            {error}
          </p>
        )}

        {hasSearched && (
          <div className="mt-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ color: theme.text }}>
                Search Results ({results.length} locations)
              </h2>

              {results.length > 1 && (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm font-semibold hover:underline"
                  style={{ color: theme.primary }}
                >
                  {selectedLocations.length === results.length ? (
                    <><FaCheckSquare size={16} />Deselect All</>
                  ) : (
                    <><FaSquare size={16} />Select All</>
                  )}
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="rounded-2xl border px-6 py-8 text-center" style={{ borderColor: subtleBorder, background: theme.card }}>
                <span className="mx-auto block h-7 w-7 animate-spin rounded-full border-2" style={{ borderColor: "rgba(2,34,241,0.3)", borderTopColor: theme.primary }} />
                <p className="mt-4 text-sm font-medium" style={{ color: theme.textMuted }}>
                  Searching locations...
                </p>
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-4">
                {results.map((loc, index) => {
                  const locationId = getBusinessId(loc) || index;
                  const locationName = getBusinessName(loc);
                  const locationAddress = getBusinessAddress(loc);
                  const locationRating = getBusinessRating(loc);
                  const reviewCount = getBusinessReviewCount(loc);

                  const isSelected = selectedLocations.some(
                    (item) => getBusinessId(item) === getBusinessId(loc)
                  );

                  return (
                    <button
                      key={locationId}
                      type="button"
                      onClick={() => toggleLocationSelection(loc)}
                      className="w-full rounded-2xl border p-5 text-left transition-colors"
                      style={{
                        borderColor: isSelected ? theme.success : subtleBorder,
                        background: theme.card,
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors"
                          style={{
                            borderColor: isSelected ? theme.success : subtleBorder,
                            background: isSelected ? theme.success : "transparent",
                          }}
                        >
                          {isSelected && <FaCheck size={14} color="#fff" />}
                        </div>

                        <div className="flex-1">
                          <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
                            {locationName}
                          </h3>

                          <div className="mt-3 flex items-start">
                            <FaMapMarkerAlt className="mt-1 shrink-0" size={16} color={theme.primary} />
                            <p className="ml-2 text-sm leading-6" style={{ color: theme.textMuted }}>
                              {locationAddress}
                            </p>
                          </div>

                          <div className="mt-4 flex items-center">
                            <FaStar className="shrink-0" size={16} color={theme.warning} />
                            <span className="ml-2 text-sm font-semibold" style={{ color: theme.text }}>
                              {locationRating.toFixed(1)}
                            </span>
                            <span className="ml-2 text-sm" style={{ color: theme.textLight }}>
                              ({reviewCount} reviews)
                            </span>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="rounded-full px-3 py-1.5" style={{ background: theme.successBg }}>
                            <span className="text-xs font-semibold" style={{ color: theme.success }}>Selected</span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border px-6 py-8 text-center" style={{ borderColor: subtleBorder, background: theme.card }}>
                <FaSearch className="mx-auto" size={24} color={theme.primary} />
                <h3 className="mt-4 text-lg font-semibold" style={{ color: theme.text }}>No locations found</h3>
                <p className="mt-2 text-sm leading-6" style={{ color: theme.textMuted }}>
                  Try searching for a business name like "Starbucks", "McDonald's", or your business name.
                </p>
              </div>
            )}
          </div>
        )}

        {selectedLocations.length > 0 && (
          <div className="sticky bottom-0 mt-8 border-t pt-4" style={{ borderColor: subtleBorder, background: theme.background }}>
            <Button variant="primary" theme={theme} onClick={handleConnectLocations} className="w-full py-4 text-base">
              {isAddLocationMode ? "Add" : "Connect"} {selectedLocations.length} Location{selectedLocations.length > 1 ? "s" : ""}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
